import Link from "next/link";

import type { SiteCopy, SiteLanguage } from "@/lib/site-data";
import { withLanguagePrefix } from "@/lib/site-locale";

/**
 * HomeFooter — the thin editorial footer.
 *
 * Originally lived only on ``/`` (hence the name). Now also rendered
 * inside ``MarkdownPage`` so the four content surfaces (About,
 * Contact, Privacy, Terms) get the same nav-and-legal block the
 * home page does. The component is pure presentation and takes its
 * own ``language`` + ``copy`` so it stays portable between routes.
 *
 * Still not rendered inside ``SiteShell`` — the wire-style index
 * pages (news, clusters, events, discover, genres, artists) stay
 * footer-free so the infinite scroll has nothing under it.
 */
export function HomeFooter({ language, copy }: { language: SiteLanguage; copy: SiteCopy }) {
  const year = new Date().getFullYear();

  return (
    <footer className="mt-16 border-t border-border pt-10">
      <div className="grid gap-8 pb-10 md:grid-cols-[1.2fr_1fr_1fr_1fr]">
        <div className="space-y-2">
          <p className="text-lg font-black tracking-tight text-foreground">duudey</p>
          <p className="max-w-xs text-xs text-muted-foreground">{copy.siteTagline}</p>
          <p className="pt-2 text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground">
            © {year} duudey
          </p>
        </div>

        <FooterColumn
          title={copy.ui.footerBrowse}
          items={[
            { label: copy.nav.news, href: withLanguagePrefix("/news", language) },
            { label: copy.nav.clusters, href: withLanguagePrefix("/clusters", language) },
            { label: copy.nav.events, href: withLanguagePrefix("/events", language) },
            { label: copy.nav.discover, href: withLanguagePrefix("/discover", language) },
          ]}
        />

        <FooterColumn
          title={copy.ui.footerCompany}
          items={[
            // Routes live at ``src/app/[lang]/{about,contact}/page.tsx``;
            // each loads its markdown body from ``src/content/<slug>``.
            { label: copy.ui.footerAbout, href: withLanguagePrefix("/about", language) },
            { label: copy.ui.footerContact, href: withLanguagePrefix("/contact", language) },
          ]}
        />

        <FooterColumn
          title={copy.ui.footerLegal}
          items={[
            // Privacy and Terms render with a "Draft — pending legal
            // review" badge until counsel signs off; the routes still
            // ship publicly so the footer is no longer a dead link.
            { label: copy.ui.footerTerms, href: withLanguagePrefix("/terms", language) },
            { label: copy.ui.footerPrivacy, href: withLanguagePrefix("/privacy", language) },
          ]}
        />
      </div>
    </footer>
  );
}

function FooterColumn({
  title,
  items,
}: {
  title: string;
  items: Array<{ label: string; href: string }>;
}) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">{title}</p>
      <ul className="space-y-1.5 text-sm text-foreground">
        {items.map((item) => (
          <li key={`${title}-${item.label}`}>
            <Link href={item.href} className="transition hover:text-rose-600 dark:hover:text-rose-300">
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
